import React, { useEffect, useState, useCallback } from "react";

import {
  OrangeDart,
  GoogleWatch,
  GoogleHeart,
  BackImg,
} from "@utils/defaultImages";
import { Button } from "@mui/material";
import { format, subDays, differenceInDays, parse, addDays } from "date-fns";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { toast } from "react-toastify";

import useMediaQueryHook from "@hooks/useMediaQueryHook";
import { useGoogleFitLogin } from "@utils/useGoogleFitLogin";
import { ConnectGoogle } from "../../components/common/Modals/ConnectGoogle";
import { connectWithGoogle } from "api/authService";
import { fetchStepsData } from "api/weeklyTracker";
import MetricChart from "@components/ui/charts/MetricChart";
import AISuggestion from "../../components/common/StepsAi";
import LoaderFilter from "../../components/common/LoaderFilter";
import ActualDietPlanLoader from "@components/ui/Loaders/ActualDietPlanLoader";
import { getDeviceCards, getStatCards } from "data/uiData/DeviceCards";
import { useTranslation } from "react-i18next";
import InfoGridCard from "@components/ui/InfoGridCard";
import DonutChart from "@components/ui/charts/DonutChart";
import LogoutDevice from "./LogoutDevice";
import { googleLogout } from "@api/progressTracker";
import FourCardsView from "@components/ui/FourCardsView";

const ExercisePlanCharts = ({ showLoader = false, setShowLoader }) => {
  const stored = localStorage.getItem("is_google_fit_connect");
  const initialValue = stored === "true";
  const { t } = useTranslation();
  const [googleFitConnect, setGoogleFitConnect] = useState(initialValue);
  const [email, setEmail] = useState("");
  const { isMobile } = useMediaQueryHook();
  const [open, setOpen] = useState(false);
  const [connectingGoogleFit, setConnectingGoogleFit] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const [goals, setGoals] = useState({
    step_goal: 0,
    goal_calories: 0,
    goal_active_minutes: 0,
  });
  // Date filter states - initialize with last 7 days
  const [dateRange, setDateRange] = useState({
    startDate: subDays(new Date(), 6),
    endDate: new Date(),
  });

  const [dateErrors, setDateErrors] = useState({
    startDate: "",
    endDate: "",
    global: "",
  });

  // Data states
  const [apiResponse, setApiResponse] = useState(null);
  const [chartData, setChartData] = useState({
    steps: [],
    calories: [],
    activeMinutes: [],
  });
  const chartLabels = {
    steps: {
      actual: "Actual Steps",
      goal: "Steps Goal",
    },
    calories: {
      actual: "Calories Burnt",
      goal: "Calories Burnt Goal",
    },
    activeMinutes: {
      actual: "Active Time",
      goal: "Active Time Goal",
    },
  };

  const [activeChartTab, setActiveChartTab] = useState("steps");

  const [summaryStats, setSummaryStats] = useState({
    totalSteps: "0",
    caloriesBurnt: "0 kcal",
    activeTime: "0m",
    distance: "0 km",
    progressPercentage: "0",
    weeklyGoalProgress: "0",
    totalDistance: "0 m",
    averageSteps: "0",
    averageCalories: "0 kcal",
    averageActiveTime: "0m",
    fat_free_mass_kg: "0 kg",
    sleep_minutes: "0 m",
    weight: "0 kg",
    muscle_mass_kg: "0 kg",
    body_fat_percentage: "0 %",
    bmi: "0",
    blood_pressure: "0 mmHg",
    heart_rate: "0 bpm",
  });
  const statCards = getStatCards(summaryStats);
  const physicalCharactersticsCard = getDeviceCards(summaryStats);

  // Format date to dd-MM-yyyy for API
  const formatDate = (date) => {
    if (!date) return "";
    return format(date, "dd-MM-yyyy");
  };

  // Parse date from API response
  const parseApiDate = (dateString) => {
    return parse(dateString, "yyyy-MM-dd", new Date());
  };

  // Get system timezone
  const getSystemTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  };

  // Function to set date range to latest 7 days
  const setLatest7Days = useCallback(() => {
    const today = new Date();
    const sevenDaysAgo = subDays(today, 6);

    setDateRange({
      startDate: sevenDaysAgo,
      endDate: today,
    });

    // Clear any date errors
    setDateErrors({
      startDate: "",
      endDate: "",
      global: "",
    });

    return { startDate: sevenDaysAgo, endDate: today };
  }, []);

  // Handle Sync Now button click
  const handleSyncNow = async () => {
    const newDateRange = setLatest7Days();
    await handleFetchData(newDateRange.startDate, newDateRange.endDate);
  };

  // Handle date change with validation
  const handleDateChange = (date, type) => {
    if (!date) return;

    setDateRange((prev) => ({
      ...prev,
      [type]: date,
    }));
  };

  // Main data fetching function with explicit parameters
  const handleFetchData = async (
    start = dateRange.startDate,
    end = dateRange.endDate
  ) => {
    // Clear all errors first
    setDateErrors({
      startDate: "",
      endDate: "",
      global: "",
    });

    setDataLoading(true);
    try {
      const payload = {
        durationMillis: 86400000,
        startTimeMillis: formatDate(start),
        endTimeMillis: formatDate(end),
        time_zone: getSystemTimezone(),
      };

      const response = await fetchStepsData(payload);
      setEmail(response.email || "");
      if (response?.summary) {
        if (response?.summary?.goals) {
          setGoals(response?.summary?.goals);
        }
        setApiResponse(response.summary);

        // Update last sync time
        if (response.summary?.last_sync) {
          setLastSyncTime(response.summary.last_sync);
        }

        // Process chart data
        processChartData(
          response.summary?.graph_data || response.summary?.graphData || {}
        );

        // Process summary stats
        processSummaryStats(response.summary);

        setHasData(true);
      } else {
        setApiResponse(null);
        setChartData({ steps: [], calories: [], activeMinutes: [] });
        setSummaryStats({
          totalSteps: "0",
          caloriesBurnt: "0 kcal",
          activeTime: "0m",
          distance: "0 km",
          progressPercentage: "0",
          weeklyGoalProgress: "0",
          totalDistance: "0 m",
          averageSteps: "0",
          averageCalories: "0 kcal",
          averageActiveTime: "0m",
        });
        setHasData(false);
        toast.info("No data available for the selected date range");
      }
    } catch (error) {
      console.error("Error in fetching data", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to fetch data. Please try again."
      );
      setHasData(false);
    } finally {
      setDataLoading(false);
      if (setShowLoader) {
        setShowLoader(false);
      }
    }
  };

  const processChartData = (graphData) => {
    if (!graphData || Object.keys(graphData).length === 0) {
      setChartData({ steps: [], calories: [], activeMinutes: [] });
      return;
    }

    // Check for new structure with separate arrays
    if (graphData.steps && graphData.calories && graphData.active_minutes) {
      const stepsData = graphData.steps.map((item) => ({
        name: item.name,
        steps: item.value || 0,
      }));

      const caloriesData = graphData.calories.map((item) => ({
        name: item.name,
        calories: item.value ? Math.round(item.value) : 0,
      }));

      const activeMinutesData = graphData.active_minutes.map((item) => ({
        name: item.name,
        activeMinutes: item.value || 0,
      }));

      setChartData({
        steps: stepsData,
        calories: caloriesData,
        activeMinutes: activeMinutesData,
      });
    } else if (Array.isArray(graphData)) {
      // Fallback to old structure
      const processedData = graphData.map((item) => {
        const date = parseApiDate(item.name);
        return {
          name: item.name,
          steps: item.steps || 0,
          calories: item.calories || 0,
          activeMinutes: item.active_minutes || 0,
        };
      });

      setChartData({
        steps: processedData,
        calories: processedData,
        activeMinutes: processedData,
      });
    }
  };

  const processSummaryStats = (summary) => {
    if (!summary) {
      setSummaryStats({
        totalSteps: "0",
        caloriesBurnt: "0 kcal",
        activeTime: "0m",
        distance: "0 km",
        progressPercentage: "0",
        weeklyGoalProgress: "0",
        totalDistance: "0 m",
        averageSteps: "0",
        averageCalories: "0 kcal",
        averageActiveTime: "0m",
      });
      return;
    }

    // Helper functions
    const formatActiveTime = (minutes) => {
      if (!minutes) return "0m";
      const hours = Math.floor(minutes / 60);
      const mins = Math.round(minutes % 60);
      return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
    };

    const formatCalories = (calories) => {
      if (!calories) return "0 kcal";
      return `${parseFloat(calories).toFixed(0)} kcal`;
    };

    const formatDistance = (distance) => {
      if (!distance) return "0 m";
      if (distance >= 1000) {
        return `${(distance / 1000).toFixed(2)} km`;
      }
      return `${Math.round(distance)} m`;
    };

    setSummaryStats({
      totalSteps: summary.totals?.steps?.toLocaleString() || "0",
      caloriesBurnt: formatCalories(summary.totals?.calories) || "0 kcal",
      activeTime: formatActiveTime(summary.totals?.active_minutes) || "0m",
      distance: summary?.totals?.distance
        ? (summary.totals.distance / 1000).toFixed(2) + " km"
        : "0 km",
      progressPercentage: summary.weekly_goal_progress || "0",
      weeklyGoalProgress: summary.weekly_goal_progress || "0",
      totalDistance: formatDistance(summary.totals?.distance) || "0 m",
      averageSteps: summary.averages?.steps?.toLocaleString() || "0",
      averageCalories: formatCalories(summary.averages?.calories) || "0 kcal",
      averageActiveTime:
        formatActiveTime(summary.averages?.active_minutes) || "0m",
      fat_free_mass_kg:
        summary.averages?.fat_free_mass_kg != null
          ? `${summary.averages.fat_free_mass_kg} kg`
          : "0 kg",

      sleep_minutes:
        summary.averages?.sleep_minutes != null
          ? `${summary.averages.sleep_minutes}`
          : "0h 0m",

      weight:
        summary.averages?.weight != null
          ? `${summary.averages.weight} kg`
          : "0 kg",

      muscle_mass_kg:
        summary.averages?.muscle_mass_kg != null
          ? `${summary.averages.muscle_mass_kg} kg`
          : "0 kg",

      body_fat_percentage:
        summary.averages?.body_fat_percentage != null
          ? `${summary.averages.body_fat_percentage}%`
          : "0 %",

      bmi: summary.averages?.bmi ?? "0",

      blood_pressure: `${
        summary.averages?.blood_pressure?.latest?.systolic ?? 0
      }/${summary.averages?.blood_pressure?.latest?.diastolic ?? 0} mmHg`,

      heart_rate:
        summary.averages?.heart_rate != null
          ? `${summary.averages.heart_rate} bpm`
          : "0 bpm",
    });
  };

  const handleSuccess = async (codeResponse) => {
    setOpen(false);
    try {
      setConnectingGoogleFit(true);
      await connectWithGoogle(codeResponse?.code);
      setGoogleFitConnect(true);
      localStorage.setItem("is_google_fit_connect", "true");

      // Set to latest 7 days and fetch data
      const newDateRange = setLatest7Days();
      await handleFetchData(newDateRange.startDate, newDateRange.endDate);

      setConnectingGoogleFit(false);
    } catch (error) {
      console.error("❌ Error sending code to backend:", error);
      toast.error(
        error.response?.data?.message ||
          "Google login failed. Please try again."
      );
      setConnectingGoogleFit(false);
    }
  };

  const loginGoogleFit = useGoogleFitLogin(handleSuccess);

  const handleOpenGoogle = async () => {
    try {
      loginGoogleFit();
    } catch (error) {
      console.error("Error in google authentication", error);
    }
  };

  // Format last sync time for display
  const formatLastSync = () => {
    if (!lastSyncTime) return "";
    try {
      const date = new Date(lastSyncTime);
      return format(date, "dd-MM-yyyy 'at' hh:mm a");
    } catch (error) {
      return "Invalid date";
    }
  };

  // Auto-fetch data when component mounts and Google Fit is connected
  useEffect(() => {
    if (googleFitConnect) {
      handleFetchData();
    }
  }, [googleFitConnect]);

  // Calculate days selected - moved inside component
  const daysSelected =
    dateRange.startDate && dateRange.endDate
      ? differenceInDays(dateRange.endDate, dateRange.startDate) + 1
      : 0;

  if (showLoader) {
    return <ActualDietPlanLoader progressTracker={true} />;
  }

  const handleLogout = async () => {
    setLogoutLoading(true);
    setGoogleFitConnect(false);
    localStorage.setItem("is_google_fit_connect", "false");
    try {
      await googleLogout();
      toast.info("Successfully logged out from Google Fit.");
    } catch (error) {
      console.log("Error in logging out");
      toast.error("Error in logging out. Please try again.");
    } finally {
      setLogoutLoading(false);
    }
  };

  // Show empty state when no data is available
  const renderNoDataState = () => (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="text-center mb-6">
        <div className="text-4xl mb-2">📊</div>
        <h3 className="text-xl font-semibold text-gray-700 mb-2">
          No Data Available
        </h3>
        <p className="text-white-500">
          Select a date range (max 7 days) and click "Apply Filter" to load data
        </p>
      </div>
    </div>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <div
        className={`bg-cover p-6 rounded-3xl text-white min-h-[500px] relative transition-opacity duration-300 ${
          (connectingGoogleFit || dataLoading) && !hasData
            ? "opacity-50 pointer-events-none"
            : ""
        }`}
        style={{ backgroundImage: `url(${BackImg})` }}
      >
        {!googleFitConnect ? (
          <>
            <div className="flex justify-center items-center flex-col">
              <img
                src={GoogleWatch}
                alt="Watch Image"
                className="min-h-[200px]"
              />
              <h1 className="text-[20px] font-[600] text-white font-[Lato] mt-3">
                Set Up Your Activity Tracking
              </h1>
              <p className="text-[16px] font-[Lato] font-[400] mt-3">
                Enable syncing to see your activity progress here.
              </p>

              <button
                className="bg-[#64DDFF] py-[12px] px-6 text-white rounded-full mt-6 flex items-center gap-2 hover:opacity-90 transition-opacity"
                onClick={() => setOpen(true)}
                disabled={connectingGoogleFit}
              >
                {connectingGoogleFit
                  ? "Connecting..."
                  : "Connect via Google Fit"}
                {!connectingGoogleFit && (
                  <img src={GoogleHeart} alt="GoogleHeart" />
                )}
              </button>
            </div>

            {connectingGoogleFit && (
              <div className="absolute inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 rounded-3xl">
                <div className="flex flex-col items-center">
                  <svg
                    className="animate-spin h-12 w-12 text-white mb-4"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <p className="text-white text-lg">
                    Connecting to Google Fit...
                  </p>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Data Loading Overlay */}
            {!hasData && dataLoading && googleFitConnect && (
              <div className="absolute inset-0 flex items-center justify-center z-40 bg-black bg-opacity-50 rounded-3xl">
                <div className="flex flex-col items-center">
                  <svg
                    className="animate-spin h-10 w-10 text-white mb-2"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <p className="text-white text-sm">Loading data...</p>
                </div>
              </div>
            )}

            {hasData && (
              <h2 className="text-[16px] font-[500] font-[Lato] mb-2 md:mb-3 text-center">
                Your Connected Health Stats
              </h2>
            )}
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between md:items-center mb-2 md:mb-6 gap-2">
              {hasData && (
                <div className="flex flex-col xl:flex-row xl::items-center md:justify-end gap-2 flex-1">
                  <LoaderFilter
                    startDate={dateRange.startDate}
                    endDate={dateRange.endDate}
                    onDateChange={handleDateChange}
                    onApplyFilter={handleFetchData}
                    loading={dataLoading}
                    dateErrors={dateErrors}
                    daysSelected={daysSelected}
                  />
                  <div className="min-w-[170px] flex justify-between items-center gap-3 bg-white rounded-lg px-2 py-[6px] flex-1">
                    <div className="text-[12px] font-[Lato] text-[#6C6A6A]">
                      Last Sync: {formatLastSync()}
                    </div>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSyncNow}
                      disabled={dataLoading}
                      sx={{
                        backgroundColor: "#0575E6",
                        "&:hover": {
                          backgroundColor: "#0461c4",
                          boxShadow: "0 4px 10px rgba(5, 117, 230, 0.4)",
                        },
                        "&:disabled": {
                          backgroundColor: "#6b7280",
                          color: "#cccccc",
                        },
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 700,
                        minWidth: "80px",
                      }}
                    >
                      Sync Now
                    </Button>
                  </div>
                  <LogoutDevice
                    logoutLoading={logoutLoading}
                    email={email}
                    onLogout={handleLogout}
                  />
                </div>
              )}
              {/* Sync Info for Desktop */}
            </div>

            {hasData && (
              <>
                {/* Main Content - Show only when we have data */}
                {hasData ? (
                  <>
                    {/* Stats Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-10 xl:gap-20">
                      {statCards.map((stat, index) => (
                        <div
                          key={index}
                          className="bg-white rounded-2xl p-4 text-[#0575e6] flex flex-col xl:flex-row items-center justify-center gap-3"
                        >
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: stat.color }}
                          >
                            {stat.icon}
                          </div>
                          <div>
                            <div className="text-[12px] font-[Lato] text-[#898787] text-center xl:text-start">
                              {stat.label}
                            </div>
                            <div className="text-[#6C6A6A] text-[16px] font-[Lato] text-center xl:text-start">
                              {stat.value}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Chart Section */}
                    <div className="mt-2 md:mt-6 grid grid-cols-1">
                      <div className="bg-white rounded-2xl p-6">
                        <div className="flex items-center justify-center gap-5">
                          <div className="flex items-center justify-center gap-1">
                            <div className="bg-[#05CFE6] w-4 h-4 rounded-full"></div>
                            <h2 className="text-[12px] font-[600] font-[Lato] text-[#444444]">
                              {chartLabels[activeChartTab]?.actual}
                            </h2>
                          </div>

                          <div className="flex items-center justify-center gap-1 my-2">
                            <div className="bg-[#FF8B38] w-4 h-4 rounded-full"></div>
                            <h2 className="text-[12px] font-[600] font-[Lato] text-[#444444]">
                              {chartLabels[activeChartTab]?.goal}
                            </h2>
                          </div>
                        </div>

                        {/* Chart Tabs */}
                        <div className="flex flex-col md:flex-row bg-white rounded-full p-1 md:border md:border-[#E9E9E9] w-full gap-1 mt-1 mb-3">
                          {["steps", "calories", "activeMinutes"].map((tab) => (
                            <button
                              key={tab}
                              className={`${
                                activeChartTab === tab
                                  ? "bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white"
                                  : "text-gray-600 hover:bg-gray-100"
                              } px-4 py-2 rounded-full text-[10px] md:text-[12px] font-medium flex-1 border border-[#E9E9E9] md:border-none capitalize`}
                              onClick={() => setActiveChartTab(tab)}
                            >
                              {tab === "activeMinutes" ? "Active Time" : tab}
                            </button>
                          ))}
                        </div>

                        {/* Chart */}
                        {chartData[activeChartTab]?.length > 0 ? (
                          <MetricChart
                            data={chartData}
                            activeTab={activeChartTab}
                            isMobile={isMobile}
                            advisedActiveTime={goals?.goal_active_minutes}
                            advisedCaloriesBurnt={goals?.goal_calories}
                            advisedSteps={goals?.step_goal}
                          />
                        ) : (
                          <div className="flex items-center justify-center h-[300px]">
                            <p className="text-gray-500">
                              No chart data available
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 xl:grid-cols-2 mt-2 md:mt-6 gap-3">
                      {/* <div className="hidden md:block">
                        <InfoGridCard
                          data={physicalCharactersticsCard}
                          columns={4}
                          gap="gap-2"
                          rounded="rounded-[17px]"
                        />
                      </div>
                      <div className="block md:hidden">
                        <InfoGridCard
                          data={physicalCharactersticsCard}
                          columns={2}
                          gap="gap-2"
                          rounded="rounded-[17px]"
                        />
                      </div> */}
                      <FourCardsView
                        data={physicalCharactersticsCard}
                        columns={4}
                        gap="gap-2"
                        rounded="rounded-[17px]"
                      />

                      {/* Suggestion Section */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:mt-3 xl:mt-0">
                        <div className="bg-white rounded-[17px] h-full p-4">
                          <div className="flex items-center gap-3">
                            <OrangeDart className="w-5 h-5" />
                            <h2 className="font-[600] font-[Lato] text-[12px] text-[#444444]">
                              Your fitness goal progress this week
                            </h2>
                          </div>

                          <DonutChart
                            percentage={summaryStats.weeklyGoalProgress}
                            title="Weekly Goal"
                          />
                          <div className="flex items-center justify-center gap-1 my-2">
                            <div className="bg-[#FF8B38] w-4 h-4 rounded-full"></div>
                            <h2 className="text-[12px] font-[600] font-[Lato] text-[#444444]">
                              Goal:
                            </h2>
                            <p className="text-[12px] font-[400] font-[Lato] text-[#444444]">
                              {goals?.step_goal * chartData?.steps?.length}{" "}
                              steps per week
                            </p>
                          </div>
                          <div className="flex items-center justify-center gap-1">
                            <div className="bg-[#05CFE6] w-4 h-4 rounded-full"></div>
                            <h2 className="text-[12px] font-[600] font-[Lato] text-[#444444]">
                              Completed:
                            </h2>
                            <p className="text-[12px] font-[400] font-[Lato] text-[#444444]">
                              {summaryStats?.totalSteps} steps
                            </p>
                          </div>
                        </div>
                        <AISuggestion
                          fitnessData={summaryStats}
                          dateRange={{
                            startDate: dateRange.startDate,
                            endDate: dateRange.endDate,
                            daysSelected,
                          }}
                          chartData={chartData}
                          apiResponse={apiResponse}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  renderNoDataState()
                )}
              </>
            )}
          </>
        )}

        {open && (
          <ConnectGoogle
            open={open}
            setOpen={setOpen}
            handleOpenGoogle={handleOpenGoogle}
            loading={connectingGoogleFit}
          />
        )}
      </div>
    </LocalizationProvider>
  );
};

export default ExercisePlanCharts;


// import React, { useEffect, useState, useCallback } from "react";
// import {
//   OrangeDart,
//   GoogleWatch,
//   GoogleHeart,
//   BackImg,
// } from "@utils/defaultImages";
// import { Button } from "@mui/material";
// import { format, parse, isWithinInterval, eachDayOfInterval, isSameDay, isBefore, isAfter } from "date-fns";
// import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
// import { LocalizationProvider } from "@mui/x-date-pickers";
// import { toast } from "react-toastify";

// import useMediaQueryHook from "@hooks/useMediaQueryHook";
// import { useGoogleFitLogin } from "@utils/useGoogleFitLogin";
// import { ConnectGoogle } from "../../components/common/Modals/ConnectGoogle";
// import { connectWithGoogle } from "api/authService";
// import { fetchStepsData } from "api/weeklyTracker";
// import MetricChart from "@components/ui/charts/MetricChart";
// import AISuggestion from "../../components/common/StepsAi";
// import ActualDietPlanLoader from "@components/ui/Loaders/ActualDietPlanLoader";
// import { getDeviceCards, getStatCards } from "data/uiData/DeviceCards";
// import { useTranslation } from "react-i18next";
// import DonutChart from "@components/ui/charts/DonutChart";
// import LogoutDevice from "./LogoutDevice";
// import { googleLogout } from "@api/progressTracker";
// import FourCardsView from "@components/ui/FourCardsView";
// import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
// import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
// import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
// import {
//   Popover,
//   Box,
//   Typography,
//   IconButton,
// } from "@mui/material";
// import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
// import { PickersDay } from "@mui/x-date-pickers/PickersDay";

// const ExercisePlanCharts = ({ showLoader = false, setShowLoader }) => {
//   const stored = localStorage.getItem("is_google_fit_connect");
//   const initialValue = stored === "true";
//   const { t } = useTranslation();
//   const [googleFitConnect, setGoogleFitConnect] = useState(initialValue);
//   const [email, setEmail] = useState("");
//   const { isMobile } = useMediaQueryHook();
//   const [open, setOpen] = useState(false);
//   const [connectingGoogleFit, setConnectingGoogleFit] = useState(false);
//   const [dataLoading, setDataLoading] = useState(false);
//   const [hasData, setHasData] = useState(false);
//   const [lastSyncTime, setLastSyncTime] = useState(null);
//   const [logoutLoading, setLogoutLoading] = useState(false);
//   const [goals, setGoals] = useState({
//     step_goal: 0,
//     goal_calories: 0,
//     goal_active_minutes: 0,
//   });
  
//   // Week navigation states
//   const [currentWeekIndex, setCurrentWeekIndex] = useState(0);
//   const [weeklyData, setWeeklyData] = useState([]);
//   const [overallData, setOverallData] = useState(null);
  
//   // Calendar states
//   const [calendarAnchor, setCalendarAnchor] = useState(null);
//   const [calendarDate, setCalendarDate] = useState(new Date());
  
//   // Current week date range
//   const [currentWeekRange, setCurrentWeekRange] = useState({
//     startDate: null,
//     endDate: null,
//   });

//   // Data states
//   const [chartData, setChartData] = useState({
//     steps: [],
//     calories: [],
//     activeMinutes: [],
//   });
  
//   const chartLabels = {
//     steps: {
//       actual: "Actual Steps",
//       goal: "Steps Goal",
//     },
//     calories: {
//       actual: "Calories Burnt",
//       goal: "Calories Burnt Goal",
//     },
//     activeMinutes: {
//       actual: "Active Time",
//       goal: "Active Time Goal",
//     },
//   };

//   const [activeChartTab, setActiveChartTab] = useState("steps");

//   const [summaryStats, setSummaryStats] = useState({
//     totalSteps: "0",
//     caloriesBurnt: "0 kcal",
//     activeTime: "0m",
//     distance: "0 km",
//     progressPercentage: "0",
//     weeklyGoalProgress: "0",
//     totalDistance: "0 m",
//     averageSteps: "0",
//     averageCalories: "0 kcal",
//     averageActiveTime: "0m",
//     fat_free_mass_kg: "0 kg",
//     sleep_minutes: "0 m",
//     weight: "0 kg",
//     muscle_mass_kg: "0 kg",
//     body_fat_percentage: "0 %",
//     bmi: "0",
//     blood_pressure: "0 mmHg",
//     heart_rate: "0 bpm",
//   });
  
//   const statCards = getStatCards(summaryStats);
//   const physicalCharactersticsCard = getDeviceCards(summaryStats);

//   // Get all enabled dates from weekly data for calendar
//   const getEnabledDates = () => {
//     const enabledDates = [];
//     weeklyData.forEach(week => {
//       if (week.week_start && week.week_end) {
//         const start = parse(week.week_start, 'yyyy-MM-dd', new Date());
//         const end = parse(week.week_end, 'yyyy-MM-dd', new Date());
//         const daysInWeek = eachDayOfInterval({ start, end });
//         enabledDates.push(...daysInWeek);
//       }
//     });
//     return enabledDates;
//   };

//   // Check if a date is enabled
//   const isDateEnabled = (date) => {
//     const enabledDates = getEnabledDates();
//     return enabledDates.some(enabledDate => isSameDay(enabledDate, date));
//   };

//   // Find week index for a specific date
//   const findWeekIndexForDate = (date) => {
//     return weeklyData.findIndex(week => {
//       if (week.week_start && week.week_end) {
//         const start = parse(week.week_start, 'yyyy-MM-dd', new Date());
//         const end = parse(week.week_end, 'yyyy-MM-dd', new Date());
//         return isWithinInterval(date, { start, end });
//       }
//       return false;
//     });
//   };

//   // Get system timezone
//   const getSystemTimezone = () => {
//     return Intl.DateTimeFormat().resolvedOptions().timeZone;
//   };

//   // Process the new API response structure
//   const processApiResponse = (response) => {
//     if (!response || !response.weekly_summaries || response.weekly_summaries.length === 0) {
//       return;
//     }

//     // Store all weekly data - sort by week_start descending (newest first = current week)
//     const sortedWeeklyData = [...response.weekly_summaries].sort((a, b) => {
//       const dateA = parse(a.week_start, 'yyyy-MM-dd', new Date());
//       const dateB = parse(b.week_start, 'yyyy-MM-dd', new Date());
//       return dateB - dateA; // Newest first (current week at index 0)
//     });
    
//     setWeeklyData(sortedWeeklyData);
//     setOverallData(response.overall_summary || response.overall);
    
//     // Set goals from overall data
//     if (response.goals) {
//       setGoals(response.goals);
//     } else if (response.overall_summary?.goals) {
//       setGoals(response.overall_summary.goals);
//     }
    
//     // Show first week (current week) by default
//     showWeekData(0, sortedWeeklyData);
    
//     // Set calendar date to current week's start date
//     if (sortedWeeklyData[0]?.week_start) {
//       setCalendarDate(parse(sortedWeeklyData[0].week_start, 'yyyy-MM-dd', new Date()));
//     }
//   };

//   // Show data for a specific week index
//   const showWeekData = (weekIndex, weeklySummaries = weeklyData) => {
//     if (!weeklySummaries || weeklySummaries.length === 0 || weekIndex < 0 || weekIndex >= weeklySummaries.length) {
//       return;
//     }

//     const weekData = weeklySummaries[weekIndex];
//     setCurrentWeekIndex(weekIndex);
    
//     // Update current week range
//     if (weekData.week_start && weekData.week_end) {
//       const startDate = parse(weekData.week_start, 'yyyy-MM-dd', new Date());
//       const endDate = parse(weekData.week_end, 'yyyy-MM-dd', new Date());
//       setCurrentWeekRange({
//         startDate,
//         endDate
//       });
      
//       // Update calendar date to week start
//       setCalendarDate(startDate);
//     }

//     // Process chart data
//     if (weekData.summary?.graph_data) {
//       const graphData = weekData.summary.graph_data;
      
//       const stepsData = graphData.steps?.map((item) => ({
//         name: item.name,
//         steps: item.value || 0,
//       })) || [];

//       const caloriesData = graphData.calories?.map((item) => ({
//         name: item.name,
//         calories: item.value ? Math.round(item.value) : 0,
//       })) || [];

//       const activeMinutesData = graphData.active_minutes?.map((item) => ({
//         name: item.name,
//         activeMinutes: item.value || 0,
//       })) || [];

//       setChartData({
//         steps: stepsData,
//         calories: caloriesData,
//         activeMinutes: activeMinutesData,
//       });
//     }

//     // Process summary stats for this week
//     if (weekData.summary) {
//       processSummaryStats(weekData.summary);
//     }

//     setHasData(true);
//   };

//   // Navigate to PREVIOUS week (older in time - index increases)
//   const handlePreviousWeek = () => {
//     if (currentWeekIndex < weeklyData.length - 1) {
//       showWeekData(currentWeekIndex + 1);
//     }
//   };

//   // Navigate to NEXT week (newer in time - index decreases)
//   const handleNextWeek = () => {
//     if (currentWeekIndex > 0) {
//       showWeekData(currentWeekIndex - 1);
//     }
//   };

//   // Open calendar popover
//   const handleOpenCalendar = (event) => {
//     setCalendarAnchor(event.currentTarget);
//   };

//   // Close calendar popover
//   const handleCloseCalendar = () => {
//     setCalendarAnchor(null);
//   };

//   // Handle date selection from calendar
//   const handleDateSelect = (date) => {
//     if (isDateEnabled(date)) {
//       const weekIndex = findWeekIndexForDate(date);
//       if (weekIndex !== -1) {
//         showWeekData(weekIndex);
//       }
//       handleCloseCalendar();
//     }
//   };

//   // Custom day component for calendar to show enabled dates
//   const renderDay = (day, selectedDays, pickersDayProps) => {
//     const enabled = isDateEnabled(day);
//     const isCurrentWeek = currentWeekRange.startDate && currentWeekRange.endDate && 
//                          isWithinInterval(day, { start: currentWeekRange.startDate, end: currentWeekRange.endDate });
    
//     return (
//       <PickersDay
//         {...pickersDayProps}
//         disabled={!enabled}
//         day={day}
//         sx={{
//           ...(enabled && {
//             backgroundColor: isCurrentWeek ? '#0575E6' : 'transparent',
//             color: isCurrentWeek ? 'white' : 'inherit',
//             '&:hover': {
//               backgroundColor: isCurrentWeek ? '#0461c4' : '#f0f0f0',
//             },
//             '&.Mui-selected': {
//               backgroundColor: '#0575E6',
//               color: 'white',
//             },
//             '&.Mui-disabled': {
//               opacity: 0.3,
//             }
//           })
//         }}
//       />
//     );
//   };

//   // Format week range for display
//   const formatWeekDisplay = () => {
//     if (currentWeekRange.startDate && currentWeekRange.endDate) {
//       return `${format(currentWeekRange.startDate, 'dd MMM')} - ${format(currentWeekRange.endDate, 'dd MMM yyyy')}`;
//     }
//     return "Loading...";
//   };

//   // Helper to get week navigation status text
//   const getWeekNavigationStatus = () => {
//     if (!weeklyData.length) return "";
    
//     const currentWeekNum = weeklyData[currentWeekIndex]?.week_number;
//     const totalWeeks = weeklyData.length;
    
//     return `Week ${currentWeekNum || currentWeekIndex + 1} of ${totalWeeks}`;
//   };

//   const processSummaryStats = (summary) => {
//     if (!summary) {
//       setDefaultSummaryStats();
//       return;
//     }

//     // Helper functions
//     const formatActiveTime = (minutes) => {
//       if (!minutes) return "0m";
//       const hours = Math.floor(minutes / 60);
//       const mins = Math.round(minutes % 60);
//       return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
//     };

//     const formatCalories = (calories) => {
//       if (!calories) return "0 kcal";
//       return `${parseFloat(calories).toFixed(0)} kcal`;
//     };

//     const formatDistance = (distance) => {
//       if (!distance) return "0 m";
//       if (distance >= 1000) {
//         return `${(distance / 1000).toFixed(2)} km`;
//       }
//       return `${Math.round(distance)} m`;
//     };

//     setSummaryStats({
//       totalSteps: summary.totals?.steps?.toLocaleString() || "0",
//       caloriesBurnt: formatCalories(summary.totals?.calories) || "0 kcal",
//       activeTime: formatActiveTime(summary.totals?.active_minutes) || "0m",
//       distance: summary?.totals?.distance
//         ? (summary.totals.distance / 1000).toFixed(2) + " km"
//         : "0 km",
//       progressPercentage: summary.weekly_goal_progress || "0",
//       weeklyGoalProgress: summary.weekly_goal_progress || "0",
//       totalDistance: formatDistance(summary.totals?.distance) || "0 m",
//       averageSteps: summary.averages?.steps?.toLocaleString() || "0",
//       averageCalories: formatCalories(summary.averages?.calories) || "0 kcal",
//       averageActiveTime:
//         formatActiveTime(summary.averages?.active_minutes) || "0m",
//       fat_free_mass_kg:
//         summary.averages?.fat_free_mass_kg != null
//           ? `${summary.averages.fat_free_mass_kg} kg`
//           : "0 kg",
//       sleep_minutes:
//         summary.averages?.sleep_minutes != null
//           ? `${summary.averages.sleep_minutes}`
//           : "0h 0m",
//       weight:
//         summary.averages?.weight != null
//           ? `${summary.averages.weight} kg`
//           : "0 kg",
//       muscle_mass_kg:
//         summary.averages?.muscle_mass_kg != null
//           ? `${summary.averages.muscle_mass_kg} kg`
//           : "0 kg",
//       body_fat_percentage:
//         summary.averages?.body_fat_percentage != null
//           ? `${summary.averages.body_fat_percentage}%`
//           : "0 %",
//       bmi: summary.averages?.bmi ?? "0",
//       blood_pressure: `${
//         summary.averages?.blood_pressure?.latest?.systolic ?? 0
//       }/${summary.averages?.blood_pressure?.latest?.diastolic ?? 0} mmHg`,
//       heart_rate:
//         summary.averages?.heart_rate != null
//           ? `${summary.averages.heart_rate} bpm`
//           : "0 bpm",
//     });
//   };

//   const setDefaultSummaryStats = () => {
//     setSummaryStats({
//       totalSteps: "0",
//       caloriesBurnt: "0 kcal",
//       activeTime: "0m",
//       distance: "0 km",
//       progressPercentage: "0",
//       weeklyGoalProgress: "0",
//       totalDistance: "0 m",
//       averageSteps: "0",
//       averageCalories: "0 kcal",
//       averageActiveTime: "0m",
//       fat_free_mass_kg: "0 kg",
//       sleep_minutes: "0h 0m",
//       weight: "0 kg",
//       muscle_mass_kg: "0 kg",
//       body_fat_percentage: "0 %",
//       bmi: "0",
//       blood_pressure: "0/0 mmHg",
//       heart_rate: "0 bpm",
//     });
//   };

//   // Main data fetching function
//   const handleFetchData = async () => {
//     setDataLoading(true);
//     try {
//       // Fetch all data (no date range needed as API returns all data)
//       const payload = {
//         time_zone: getSystemTimezone(),
//       };

//       const response = await fetchStepsData(payload);
//       setEmail(response.email || "");
      
//       if (response?.weekly_summaries?.length > 0) {
//         processApiResponse(response);
        
//         // Update last sync time
//         if (response.last_sync) {
//           setLastSyncTime(response.last_sync);
//         }
        
//         setHasData(true);
//       } else {
//         setWeeklyData([]);
//         setOverallData(null);
//         setDefaultSummaryStats();
//         setChartData({ steps: [], calories: [], activeMinutes: [] });
//         setHasData(false);
//         toast.info("No data available");
//       }
//     } catch (error) {
//       console.error("Error in fetching data", error);
//       toast.error(
//         error.response?.data?.message ||
//           "Failed to fetch data. Please try again."
//       );
//       setHasData(false);
//     } finally {
//       setDataLoading(false);
//       if (setShowLoader) {
//         setShowLoader(false);
//       }
//     }
//   };

//   // Handle Sync Now button click
//   const handleSyncNow = async () => {
//     await handleFetchData();
//   };

//   const handleSuccess = async (codeResponse) => {
//     setOpen(false);
//     try {
//       setConnectingGoogleFit(true);
//       await connectWithGoogle(codeResponse?.code);
//       setGoogleFitConnect(true);
//       localStorage.setItem("is_google_fit_connect", "true");

//       // Fetch data after connecting
//       await handleFetchData();

//       setConnectingGoogleFit(false);
//     } catch (error) {
//       console.error("❌ Error sending code to backend:", error);
//       toast.error(
//         error.response?.data?.message ||
//           "Google login failed. Please try again."
//       );
//       setConnectingGoogleFit(false);
//     }
//   };

//   const loginGoogleFit = useGoogleFitLogin(handleSuccess);

//   const handleOpenGoogle = async () => {
//     try {
//       loginGoogleFit();
//     } catch (error) {
//       console.error("Error in google authentication", error);
//     }
//   };

//   // Format last sync time for display
//   const formatLastSync = () => {
//     if (!lastSyncTime) return "";
//     try {
//       const date = new Date(lastSyncTime);
//       return format(date, "dd-MM-yyyy 'at' hh:mm a");
//     } catch (error) {
//       return "Invalid date";
//     }
//   };

//   // Auto-fetch data when component mounts and Google Fit is connected
//   useEffect(() => {
//     if (googleFitConnect) {
//       handleFetchData();
//     }
//   }, [googleFitConnect]);

//   if (showLoader) {
//     return <ActualDietPlanLoader progressTracker={true} />;
//   }

//   const handleLogout = async () => {
//     setLogoutLoading(true);
//     setGoogleFitConnect(false);
//     localStorage.setItem("is_google_fit_connect", "false");
//     try {
//       await googleLogout();
//       toast.info("Successfully logged out from Google Fit.");
//     } catch (error) {
//       console.log("Error in logging out");
//       toast.error("Error in logging out. Please try again.");
//     } finally {
//       setLogoutLoading(false);
//     }
//   };

//   // Show empty state when no data is available
//   const renderNoDataState = () => (
//     <div className="flex flex-col items-center justify-center py-12">
//       <div className="text-center mb-6">
//         <div className="text-4xl mb-2">📊</div>
//         <h3 className="text-xl font-semibold text-gray-700 mb-2">
//           No Data Available
//         </h3>
//         <p className="text-white-500">
//           Connect to Google Fit and sync your data to see your activity progress
//         </p>
//       </div>
//     </div>
//   );

//   return (
//     <LocalizationProvider dateAdapter={AdapterDateFns}>
//       <div
//         className={`bg-cover p-6 rounded-3xl text-white min-h-[500px] relative transition-opacity duration-300 ${
//           (connectingGoogleFit || dataLoading) && !hasData
//             ? "opacity-50 pointer-events-none"
//             : ""
//         }`}
//         style={{ backgroundImage: `url(${BackImg})` }}
//       >
//         {!googleFitConnect ? (
//           <>
//             <div className="flex justify-center items-center flex-col">
//               <img
//                 src={GoogleWatch}
//                 alt="Watch Image"
//                 className="min-h-[200px]"
//               />
//               <h1 className="text-[20px] font-[600] text-white font-[Lato] mt-3">
//                 Set Up Your Activity Tracking
//               </h1>
//               <p className="text-[16px] font-[Lato] font-[400] mt-3">
//                 Enable syncing to see your activity progress here.
//               </p>

//               <button
//                 className="bg-[#64DDFF] py-[12px] px-6 text-white rounded-full mt-6 flex items-center gap-2 hover:opacity-90 transition-opacity"
//                 onClick={() => setOpen(true)}
//                 disabled={connectingGoogleFit}
//               >
//                 {connectingGoogleFit
//                   ? "Connecting..."
//                   : "Connect via Google Fit"}
//                 {!connectingGoogleFit && (
//                   <img src={GoogleHeart} alt="GoogleHeart" />
//                 )}
//               </button>
//             </div>

//             {connectingGoogleFit && (
//               <div className="absolute inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 rounded-3xl">
//                 <div className="flex flex-col items-center">
//                   <svg
//                     className="animate-spin h-12 w-12 text-white mb-4"
//                     xmlns="http://www.w3.org/2000/svg"
//                     fill="none"
//                     viewBox="0 0 24 24"
//                   >
//                     <circle
//                       className="opacity-25"
//                       cx="12"
//                       cy="12"
//                       r="10"
//                       stroke="currentColor"
//                       strokeWidth="4"
//                     ></circle>
//                     <path
//                       className="opacity-75"
//                       fill="currentColor"
//                       d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                     ></path>
//                   </svg>
//                   <p className="text-white text-lg">
//                     Connecting to Google Fit...
//                   </p>
//                 </div>
//               </div>
//             )}
//           </>
//         ) : (
//           <>
//             {/* Data Loading Overlay */}
//             {!hasData && dataLoading && googleFitConnect && (
//               <div className="absolute inset-0 flex items-center justify-center z-40 bg-black bg-opacity-50 rounded-3xl">
//                 <div className="flex flex-col items-center">
//                   <svg
//                     className="animate-spin h-10 w-10 text-white mb-2"
//                     xmlns="http://www.w3.org/2000/svg"
//                     fill="none"
//                     viewBox="0 0 24 24"
//                   >
//                     <circle
//                       className="opacity-25"
//                       cx="12"
//                       cy="12"
//                       r="10"
//                       stroke="currentColor"
//                       strokeWidth="4"
//                     ></circle>
//                     <path
//                       className="opacity-75"
//                       fill="currentColor"
//                       d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
//                     ></path>
//                   </svg>
//                   <p className="text-white text-sm">Loading data...</p>
//                 </div>
//               </div>
//             )}

//             {hasData && (
//               <h2 className="text-[16px] font-[500] font-[Lato] mb-2 md:mb-3 text-center">
//                 Your Connected Health Stats
//               </h2>
//             )}
            
//             {/* Header with Week Navigation */}
//             <div className="flex flex-col md:flex-row justify-between md:items-center mb-2 md:mb-6 gap-2">
//               {hasData && weeklyData.length > 0 && (
//                 <div className="flex flex-col xl:flex-row xl:items-center md:justify-end gap-2 flex-1">
//                   {/* Week Navigation */}
//                   <div className="min-w-[300px] bg-white rounded-xl p-2 flex items-center justify-between shadow-md">
//                     {/* Previous Button - Goes to OLDER weeks (index increases) */}
//                     <button
//                       onClick={handlePreviousWeek}
//                       disabled={currentWeekIndex === weeklyData.length - 1 || dataLoading}
//                       className={`p-1 rounded-md ${currentWeekIndex === weeklyData.length - 1 || dataLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
//                       title="Go to previous week (older)"
//                     >
//                       <ArrowBackIosIcon style={{ fontSize: 16, color: '#6C6A6A' }} />
//                     </button>
                    
//                     <div 
//                       className="flex flex-col items-center cursor-pointer"
//                       onClick={handleOpenCalendar}
//                     >
//                       <div className="flex gap-1 md:gap-2 items-center">
//                         <h3 className="text-[#6C6A6A] text-[10px] md:text-[12px] font-[600] font-[Lato]">
//                           Week:
//                         </h3>
//                         <p className="text-[#8B8B8B] text-[10px] md:text-[12px] font-[600] font-[Lato] p-1 border border-[#E6E6E6] shadow-md">
//                           {formatWeekDisplay()}
//                         </p>
//                         <IconButton size="small">
//                           <CalendarMonthIcon style={{ fontSize: 18, color: '#0575E6' }} />
//                         </IconButton>
//                       </div>
//                       <div className="text-[10px] text-gray-500 mt-1">
//                         {getWeekNavigationStatus()}
//                       </div>
//                     </div>
                    
//                     {/* Next Button - Goes to NEWER weeks (index decreases) */}
//                     <button
//                       onClick={handleNextWeek}
//                       disabled={currentWeekIndex === 0 || dataLoading}
//                       className={`p-1 rounded-md ${currentWeekIndex === 0 || dataLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'}`}
//                       title="Go to next week (newer)"
//                     >
//                       <ArrowForwardIosIcon style={{ fontSize: 16, color: '#6C6A6A' }} />
//                     </button>
//                   </div>
                  
//                   {/* Sync Info */}
//                   <div className="min-w-[170px] flex justify-between items-center gap-3 bg-white rounded-lg px-2 py-[6px] flex-1">
//                     <div className="text-[12px] font-[Lato] text-[#6C6A6A]">
//                       Last Sync: {formatLastSync()}
//                     </div>
//                     <Button
//                       variant="contained"
//                       size="small"
//                       onClick={handleSyncNow}
//                       disabled={dataLoading}
//                       sx={{
//                         backgroundColor: "#0575E6",
//                         "&:hover": {
//                           backgroundColor: "#0461c4",
//                           boxShadow: "0 4px 10px rgba(5, 117, 230, 0.4)",
//                         },
//                         "&:disabled": {
//                           backgroundColor: "#6b7280",
//                           color: "#cccccc",
//                         },
//                         borderRadius: "8px",
//                         textTransform: "none",
//                         fontWeight: 700,
//                         minWidth: "80px",
//                       }}
//                     >
//                       Sync Now
//                     </Button>
//                   </div>
                  
//                   <LogoutDevice
//                     logoutLoading={logoutLoading}
//                     email={email}
//                     onLogout={handleLogout}
//                   />
//                 </div>
//               )}
//             </div>

//             {/* Calendar Popover */}
//             <Popover
//               open={Boolean(calendarAnchor)}
//               anchorEl={calendarAnchor}
//               onClose={handleCloseCalendar}
//               anchorOrigin={{
//                 vertical: 'bottom',
//                 horizontal: 'left',
//               }}
//               transformOrigin={{
//                 vertical: 'top',
//                 horizontal: 'left',
//               }}
//               sx={{
//                 '& .MuiPopover-paper': {
//                   borderRadius: '12px',
//                   boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
//                 },
//               }}
//             >
//               <Box sx={{ p: 2 }}>
//                 <Typography variant="h6" sx={{ fontWeight: 600, color: '#0575E6', mb: 2 }}>
//                   Select Week
//                 </Typography>
//                 <DateCalendar
//                   value={calendarDate}
//                   onChange={handleDateSelect}
//                   renderDay={renderDay}
//                   sx={{
//                     '& .MuiPickersDay-root': {
//                       fontSize: '0.875rem',
//                     },
//                   }}
//                   disabled={dataLoading}
//                 />
//                 <Typography variant="caption" sx={{ color: '#666', mt: 2, display: 'block' }}>
//                   Note: Only weeks with data are selectable
//                 </Typography>
//               </Box>
//             </Popover>

//             {hasData && weeklyData.length > 0 && (
//               <>
//                 {/* Main Content - Show only when we have data */}
//                 {hasData ? (
//                   <>
//                     {/* Stats Cards */}
//                     <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-10 xl:gap-20">
//                       {statCards.map((stat, index) => (
//                         <div
//                           key={index}
//                           className="bg-white rounded-2xl p-4 text-[#0575e6] flex flex-col xl:flex-row items-center justify-center gap-3"
//                         >
//                           <div
//                             className="w-10 h-10 rounded-full flex items-center justify-center"
//                             style={{ backgroundColor: stat.color }}
//                           >
//                             {stat.icon}
//                           </div>
//                           <div>
//                             <div className="text-[12px] font-[Lato] text-[#898787] text-center xl:text-start">
//                               {stat.label}
//                             </div>
//                             <div className="text-[#6C6A6A] text-[16px] font-[Lato] text-center xl:text-start">
//                               {stat.value}
//                             </div>
//                           </div>
//                         </div>
//                       ))}
//                     </div>

//                     {/* Chart Section */}
//                     <div className="mt-2 md:mt-6 grid grid-cols-1">
//                       <div className="bg-white rounded-2xl p-6">
//                         <div className="flex items-center justify-center gap-5">
//                           <div className="flex items-center justify-center gap-1">
//                             <div className="bg-[#05CFE6] w-4 h-4 rounded-full"></div>
//                             <h2 className="text-[12px] font-[600] font-[Lato] text-[#444444]">
//                               {chartLabels[activeChartTab]?.actual}
//                             </h2>
//                           </div>

//                           <div className="flex items-center justify-center gap-1 my-2">
//                             <div className="bg-[#FF8B38] w-4 h-4 rounded-full"></div>
//                             <h2 className="text-[12px] font-[600] font-[Lato] text-[#444444]">
//                               {chartLabels[activeChartTab]?.goal}
//                             </h2>
//                           </div>
//                         </div>

//                         {/* Chart Tabs */}
//                         <div className="flex flex-col md:flex-row bg-white rounded-full p-1 md:border md:border-[#E9E9E9] w-full gap-1 mt-1 mb-3">
//                           {["steps", "calories", "activeMinutes"].map((tab) => (
//                             <button
//                               key={tab}
//                               className={`${
//                                 activeChartTab === tab
//                                   ? "bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] text-white"
//                                   : "text-gray-600 hover:bg-gray-100"
//                               } px-4 py-2 rounded-full text-[10px] md:text-[12px] font-medium flex-1 border border-[#E9E9E9] md:border-none capitalize`}
//                               onClick={() => setActiveChartTab(tab)}
//                             >
//                               {tab === "activeMinutes" ? "Active Time" : tab}
//                             </button>
//                           ))}
//                         </div>

//                         {/* Chart */}
//                         {chartData[activeChartTab]?.length > 0 ? (
//                           <MetricChart
//                             data={chartData}
//                             activeTab={activeChartTab}
//                             isMobile={isMobile}
//                             advisedActiveTime={goals?.goal_active_minutes}
//                             advisedCaloriesBurnt={goals?.goal_calories}
//                             advisedSteps={goals?.step_goal}
//                           />
//                         ) : (
//                           <div className="flex items-center justify-center h-[300px]">
//                             <p className="text-gray-500">
//                               No chart data available for this week
//                             </p>
//                           </div>
//                         )}
//                       </div>
//                     </div>

//                     <div className="grid grid-cols-1 xl:grid-cols-2 mt-2 md:mt-6 gap-3">
//                       <FourCardsView
//                         data={physicalCharactersticsCard}
//                         columns={4}
//                         gap="gap-2"
//                         rounded="rounded-[17px]"
//                       />

//                       {/* Suggestion Section */}
//                       <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:mt-3 xl:mt-0">
//                         <div className="bg-white rounded-[17px] h-full p-4">
//                           <div className="flex items-center gap-3">
//                             <OrangeDart className="w-5 h-5" />
//                             <h2 className="font-[600] font-[Lato] text-[12px] text-[#444444]">
//                               Your fitness goal progress this week
//                             </h2>
//                           </div>

//                           <DonutChart
//                             percentage={summaryStats.weeklyGoalProgress}
//                             title="Weekly Goal"
//                           />
//                           <div className="flex items-center justify-center gap-1 my-2">
//                             <div className="bg-[#FF8B38] w-4 h-4 rounded-full"></div>
//                             <h2 className="text-[12px] font-[600] font-[Lato] text-[#444444]">
//                               Goal:
//                             </h2>
//                             <p className="text-[12px] font-[400] font-[Lato] text-[#444444]">
//                               {goals?.step_goal * 7} steps per week
//                             </p>
//                           </div>
//                           <div className="flex items-center justify-center gap-1">
//                             <div className="bg-[#05CFE6] w-4 h-4 rounded-full"></div>
//                             <h2 className="text-[12px] font-[600] font-[Lato] text-[#444444]">
//                               Completed:
//                             </h2>
//                             <p className="text-[12px] font-[400] font-[Lato] text-[#444444]">
//                               {summaryStats?.totalSteps} steps
//                             </p>
//                           </div>
//                         </div>
//                         <AISuggestion
//                           fitnessData={summaryStats}
//                           dateRange={{
//                             startDate: currentWeekRange.startDate,
//                             endDate: currentWeekRange.endDate,
//                             daysSelected: chartData.steps?.length || 0,
//                           }}
//                           chartData={chartData}
//                         />
//                       </div>
//                     </div>
//                   </>
//                 ) : (
//                   renderNoDataState()
//                 )}
//               </>
//             )}
//           </>
//         )}

//         {open && (
//           <ConnectGoogle
//             open={open}
//             setOpen={setOpen}
//             handleOpenGoogle={handleOpenGoogle}
//             loading={connectingGoogleFit}
//           />
//         )}
//       </div>
//     </LocalizationProvider>
//   );
// };

// export default ExercisePlanCharts;